<!-- src/views/billing/history/index.vue -->
<script setup lang="ts">
defineOptions({
  name: "billing_history"
});

  import { computed, h, onMounted, ref } from "vue";
  import type {
    DataTableColumns,
    DropdownOption,
    SelectOption,
  } from "naive-ui";
  import {
    NButton,
    NCard,
    NDataTable,
    NForm,
    NFormItem,
    NSelect,
    NSpace,
    NTag,
    useMessage,
  } from "naive-ui";
  import {
    fetchPaymentHistory,
    recheckPaymentStatus,
  } from "@/service/api/billing";
  import { useAppStore } from "@/store/modules/app";
  import { $t } from "@/locales";
  import { formatDateString } from "@/utils/format";
  import { format } from "@/utils/common";
  import { exportData } from "@/utils/export";
  import DownloadIcon from "@/components/icons/DownloadIcon.vue";
  import { useUserStore } from "@/store/modules/user";
  // Component state

  type PaymentStatusType = "success" | "pending" | "failed" | "processing";

  const getPaymentStatusConfig = (status: PaymentStatusType) => {
    const configs = {
      success: {
        type: "success",
        label: $t("paymentStatus.success"),
      },
      pending: {
        type: "warning",
        label: $t("paymentStatus.pending"),
      },
      failed: {
        type: "error",
        label: $t("paymentStatus.failed"),
      },
      processing: {
        type: "info",
        label: $t("paymentStatus.processing"),
      },
    } as const;

    return configs[status] || configs.pending;
  };

  const appStore = useAppStore();
  const userStore = useUserStore();
  const direction = computed(() => appStore.direction);
  const isUserAuthenticated = computed(() => userStore.isUserAuthenticated());

  const loading = ref(false);
  const recheckingPayment = ref<string | null>(null);
  // const payments = ref<App.Payment.Payment[]>([]);
  const message = useMessage();
  // Filter state
  const statusFilter = ref<string | null>(null);
  const purchaseItemFilter = ref<string | null>(null);

  // Pagination state
  const currentPage = ref(1);
  const pageSize = ref(10); // Default to 10 items per page
  // const pageSizeOptions = [5, 10, 20, 50, 100];

  // Modified state management
  const allPayments = ref<App.Payment.Payment[]>([]); // Store all payments
  const filteredPayments = computed(() => {
    let result = [...allPayments.value];

    // Apply filters
    if (statusFilter.value) {
      result = result.filter((payment) => {
        if (statusFilter.value === "confirmed") return payment.confirmed;
        if (statusFilter.value === "pending") return !payment.confirmed;
        if (statusFilter.value === "failed") {
          return payment?.bank_status === "failed";
        }
        return true;
      });
    }

    if (purchaseItemFilter.value) {
      result = result.filter(
        (payment) => payment.purchase_item === purchaseItemFilter.value
      );
    }

    return result;
  });

  const handleDownload = async (fileFormat: "csv" | "xlsx") => {
    if (!isUserAuthenticated.value) {
      window.$message?.error?.($t("page.login.common.needLogin"));
      return;
    }
    if (!filteredPayments.value || filteredPayments.value.length === 0) {
      return;
    }
    try {
      loading.value = true;
      const filename = `avalai-payment-history-${new Date().toISOString().split("T")[0]}`;

      await exportData({
        data: filteredPayments.value,
        filename,
        fileFormat,
      });

      message.success($t("common.downloadSuccess"));
    } catch (error) {
      message.error($t("common.downloadError"));
      console.error("Download error:", error);
    } finally {
      loading.value = false;
    }
  };

  const downloadOptions: DropdownOption[] = [
    {
      label: $t("common.downloadCSV"),
      key: "csv",
      props: {
        onClick: () => handleDownload("csv"),
      },
    },
    {
      label: $t("common.downloadExcel"),
      key: "xlsx",
      props: {
        onClick: () => handleDownload("xlsx"),
      },
    },
  ];
  // Filter options
  const statusOptions: SelectOption[] = [
    { label: $t("usage.all"), value: undefined },
    { label: $t("usage.success"), value: "confirmed" },
    { label: $t("usage.pending"), value: "pending" },
    { label: $t("usage.failed"), value: "failed" },
  ];

  const purchaseItemOptions: SelectOption[] = [
    { label: $t("usage.all"), value: undefined },
    { label: $t("billing.subscription"), value: "subscription" },
    { label: $t("billing.credit"), value: "credit" },
    { label: $t("billing.packages.creditPackages"), value: "credit_package" },
    // Add other purchase item types as needed
  ];

  // Utility functions
  const formatCurrency = (amount: string | null) => {
    if (!amount) return "-";
    return format(Number(amount), 0);
  };

  const formatDate = (date: string | null) => {
    if (!date) return "-";
    return formatDateString(date, "full", direction.value);
  };

  // Computed property for paginated data
  const paginatedPayments = computed(() => {
    const startIndex = (currentPage.value - 1) * pageSize.value;
    const endIndex = startIndex + pageSize.value;
    return filteredPayments.value.slice(startIndex, endIndex);
  });

  // Update total and totalPages based on filtered data
  const total = computed(() => filteredPayments.value.length);
  const totalPages = computed(() => Math.ceil(total.value / pageSize.value));

  // Modified summaries to use filtered data
  const summaries = computed(() => {
    const confirmed = filteredPayments.value.filter((p) => p.confirmed).length;
    const pending = filteredPayments.value.filter((p) => !p.confirmed).length;
    const totalAmount = filteredPayments.value.reduce(
      (sum, p) => sum + Number(p.paid_amount || 0),
      0
    );

    return {
      confirmed,
      pending,
      totalAmount,
    };
  });

  // Simplified loadPayments function
  const loadPayments = async () => {
    if (!isUserAuthenticated.value) {
      return;
    }
    try {
      loading.value = true;
      const response = await fetchPaymentHistory();

      if (response && response.data) {
        allPayments.value = response.data.results;
      }
    } catch (error) {
      message.error("Failed to fetch payment history");
      console.error("Error fetching payment history:", error);
    } finally {
      loading.value = false;
    }
  };

  // Modified handlers for client-side operations
  const handleFiltersChange = () => {
    currentPage.value = 1; // Reset to first page when filters change
  };

  // const handlePageChange = (page: number) => {
  //   currentPage.value = page;
  // };

  // const handlePageSizeChange = (size: number) => {
  //   pageSize.value = size;
  //   currentPage.value = 1;
  // };

  // Recheck payment status
  const handleRecheckPayment = async (paymentId: string) => {
    if (!isUserAuthenticated.value) {
      window.$message?.error?.($t("page.login.common.needLogin"));
      return;
    }
    try {
      recheckingPayment.value = paymentId;
      await recheckPaymentStatus({
        id: paymentId,
      });
      await loadPayments(); // Reload the list to show updated status
      message.success($t("billing.paymentStatusUpdated"));
    } catch (error) {
      message.error($t("billing.paymentStatusFailed"));
      console.error("Error rechecking payment:", error);
    } finally {
      recheckingPayment.value = null;
    }
  };
  // Table columns
  const columns: DataTableColumns<App.Payment.Payment> = [
    {
      title: $t("billing.paymentId"),
      key: "id",
      sorter: "default",
      defaultSortOrder: "descend",
      width: 100,
      render: (row) => row.id,
    },
    {
      title: $t("common.date"),
      key: "created_at",
      sorter: "default",
      minWidth: 100,
      render: (row) => formatDate(row.created_at),
    },
    {
      title: $t("usage.status"),
      key: "status",
      sorter: "default",
      minWidth: 100,
      render: (row) => {
        const getStatus = (): PaymentStatusType => {
          if (row.confirmed) return "success";
          if (row?.bank_status === "failed") return "failed";
          if (row?.bank_status === "processing") return "processing";
          return "pending";
        };

        const status = getStatus();
        const statusConfig = getPaymentStatusConfig(status);

        return h(
          "div",
          {
            class: "flex flex-col items-center gap-1",
            style: { display: "flex" },
          },
          [
            h(
              NTag,
              {
                type: statusConfig.type,
                round: true,
                style: {
                  textTransform: "capitalize",
                },
              },
              { default: () => statusConfig.label }
            ),
            !row.confirmed &&
              h(
                NButton,
                {
                  size: "tiny",
                  loading: recheckingPayment.value === row.id,
                  onClick: () => handleRecheckPayment(row.id),
                  class: "recheck-button",
                },
                { default: () => $t("common.recheck") }
              ),
          ]
        );
      },
    },
    {
      title: $t("billing.purchaseItem"),
      key: "purchase_item",
      sorter: "default",
      minWidth: 80,
      render: (row) => {
        const purchaseItemMap = {
          api: $t("common.credit"),
          credit: $t("common.credit"),
          subscription: $t("common.subscription"),
          credit_package: $t("billing.packages.creditPackages"),
        };

        return (
          purchaseItemMap[row.purchase_item as keyof typeof purchaseItemMap] ||
          "-"
        );
      },
    },
    {
      title: $t("billing.amount"),
      key: "amount",
      sorter: "default",
      minWidth: 100,
      render: (row) => formatCurrency(row.amount),
    },
    {
      title: $t("billing.discount"),
      key: "discount_amount",
      sorter: "default",
      minWidth: 100,
      render: (row) => formatCurrency(row.discount_amount),
    },
    {
      title: $t("billing.trackingCode"),
      key: "bank_tracking_code",
      minWidth: 100,
      render: (row) => row.bank_tracking_code,
    },
    {
      title: $t("billing.paidAmount"),
      key: "paid_amount",
      sorter: "default",
      minWidth: 100,
      render: (row) => formatCurrency(row.paid_amount),
    },
    {
      title: $t("billing.confirmationDate"),
      key: "confirmed_at",
      sorter: "default",
      minWidth: 120,
      render: (row) => formatDate(row.confirmed_at),
    },
  ];

  // Lifecycle hooks
  onMounted(() => {
    if (appStore.isMobile) pageSize.value = 5;
    loadPayments();
  });
</script>

<template>
  <NSpace vertical :size="16" :style="{ direction: direction }">
    <NCard>
      <template #header>
        <h2 class="text-2xl font-bold">{{ $t("billing.paymentHistory") }}</h2>
      </template>

      <NSpace vertical size="large">
        <!-- Centered Filters -->
        <div class="billing-history-filters-wrapper">
          <!-- Filters -->
          <NForm inline class="gap-3">
            <NFormItem :label="$t('usage.status')">
              <NSelect
                v-model:value="statusFilter"
                :options="statusOptions"
                :placeholder="$t('billing.selectStatus')"
                clearable
                style="width: 200px"
                :menu-props="{
                  style: { direction: direction },
                }"
                @update:value="handleFiltersChange"
              />
            </NFormItem>
            <NFormItem :label="$t('billing.purchaseItem')">
              <NSelect
                v-model:value="purchaseItemFilter"
                :options="purchaseItemOptions"
                :placeholder="$t('billing.selectItemType')"
                clearable
                style="width: 200px"
                :menu-props="{
                  style: { direction: direction },
                }"
                @update:value="handleFiltersChange"
              />
            </NFormItem>
          </NForm>

          <div>
            <!-- Download Button with Dropdown -->
            <NDropdown
              trigger="click"
              :options="downloadOptions"
              placement="bottom-end"
            >
              <NButton secondary type="info" :loading="loading" size="large">
                <template #icon>
                  <NIcon size="large">
                    <DownloadIcon />
                  </NIcon>
                </template>
                {{ $t("common.download") }}
              </NButton>
            </NDropdown>
          </div>
        </div>

        <!-- Enhanced Summary Cards -->
        <div class="billing-history-summary-wrapper">
          <NCard size="small" class="text-center">
            <div class="text-sm text-gray-600">
              {{ $t("billing.totalTransactions") }}
            </div>
            <div class="mt-2 text-xl font-bold">{{ total }}</div>
          </NCard>
          <NCard size="small" class="text-center">
            <div class="text-sm text-gray-600">
              {{ $t("billing.confirmedTransactions") }}
            </div>
            <div class="mt-2 text-xl text-success font-bold">
              {{ summaries.confirmed }}
            </div>
          </NCard>
          <NCard size="small" class="text-center">
            <div class="text-sm text-gray-600">
              {{ $t("billing.pendingTransactions") }}
            </div>
            <div class="mt-2 text-xl text-warning font-bold">
              {{ summaries.pending }}
            </div>
          </NCard>
          <NCard size="small" class="text-center">
            <div class="text-sm text-gray-600">
              {{ $t("billing.totalAmount") }}
            </div>
            <div class="mt-2 text-xl font-bold">
              {{ formatCurrency(String(summaries.totalAmount)) }}
            </div>
          </NCard>
        </div>

        <!-- Data Table with Improved Styling -->
        <NDataTable
          :columns="columns"
          :data="paginatedPayments"
          :loading="loading"
          :bordered="true"
          :single-line="false"
          striped
          :empty-text="$t('common.nothingFound')"
        />
      </NSpace>
      <template #footer>
        <NSpace
          vertical
          :size="16"
          :style="{ direction: direction }"
          justify="center"
        >
          <div v-show="totalPages" class="pagination-container">
            <NPagination v-model:page="currentPage" :page-count="totalPages" />
          </div>
        </NSpace>
      </template>
    </NCard>
  </NSpace>
</template>
